import { api } from "./client";

export interface GuildPalaceView {
  palace: string;
  name: string;
  icon: string;
  holder_guild_id: number | null;
  holder_guild_name: string | null;
}

export interface MyGuildView {
  id: number;
  name: string;
  level: number;
  member_count: number;
  palace: string;
  role: string;
  contribution: number;
  gold_treasury: number;
  can_manage_applications: boolean;
  can_manage_roster: boolean;
  can_promote: boolean;
  guild_chat_code: string | null;
}

export interface GuildRosterSlotView {
  slot: number;
  user_id: number | null;
  display_name: string | null;
  hero_class: string | null;
  is_npc: boolean;
  npc_tier: number | null;
}

export interface GuildBattleCurrentMatch {
  id: string;
  type: string;
  palace?: string;
  seat_index?: number;
  bracket?: string;
  round?: number;
  guild_a_id?: number;
  guild_b_id?: number | null;
  guild_a_name?: string;
  guild_b_name?: string;
  team_a?: string[];
  team_b?: string[];
  log?: unknown[];
  winner_side?: number;
  winner_user_id?: number;
  user_a_id?: number;
  user_b_id?: number;
  champion_id?: number;
  defender_user_id?: number | null;
  bracket_done?: boolean;
  participant_user_ids?: number[];
  participant_user_ids_a?: number[];
  participant_user_ids_b?: number[];
  is_war_final?: boolean;
  roster_a?: BattleBracketRosterEntry[];
  roster_b?: BattleBracketRosterEntry[];
}

export interface BattleBracketRosterEntry {
  label: string;
  status: "active" | "reserve" | "fallen";
  uid?: string;
}

export interface BattleBracketView {
  mode: "tournament" | "line_war" | "platinum";
  rounds?: {
    guild_a_id?: number;
    guild_b_id?: number | null;
    guild_a_name?: string;
    guild_b_name?: string | null;
    winner_guild_id?: number | null;
    winner_guild_name?: string | null;
    status?: string;
  }[];
  /** Attacker tournament rounds shown alongside line_war bracket */
  tournament_rounds?: {
    guild_a_id?: number;
    guild_b_id?: number | null;
    guild_a_name?: string;
    guild_b_name?: string | null;
    winner_guild_id?: number | null;
    winner_guild_name?: string | null;
    status?: string;
  }[];
  guild_a_id?: number;
  guild_b_id?: number | null;
  guild_a_name?: string;
  guild_b_name?: string;
  roster_a?: BattleBracketRosterEntry[];
  roster_b?: BattleBracketRosterEntry[];
  round_num?: number;
  revealed_rounds?: number;
  done?: boolean;
  pending?: boolean;
  seat_index?: number;
  incumbent_id?: number | null;
  incumbent_label?: string;
  contenders?: string[];
  contender_winners?: string[];
}

export interface GuildBattleMatchSummary {
  type?: string;
  round?: number;
  winner_label?: string;
  loser_label?: string;
  palace?: string;
  seat_index?: number;
}

export interface RosterReadySlot {
  slot: number;
  ready: boolean;
  locked: boolean;
  reason: string | null;
}

export interface PlatinumSeatClaimDetail {
  seat_index: number;
  count: number;
  claimants?: { user_id: number; display_name: string }[];
}

export interface GuildBattleLiveView {
  active: boolean;
  week_key: string | null;
  phase: string | null;
  phase_label: string | null;
  status: string | null;
  phase_ends_at: string | null;
  seconds_remaining: number;
  checkin_open: boolean;
  checkin_count: number;
  entered_battle: boolean;
  target_palace: string | null;
  message: string | null;
  last_phase_log?: unknown[];
  platinum_seat_claims: PlatinumSeatClaimDetail[];
  my_platinum_seat_claim: number | null;
  current_match: GuildBattleCurrentMatch | null;
  match_queue?: GuildBattleCurrentMatch[];
  watch_index?: number;
  palace_watch?: boolean;
  platinum_watch?: boolean;
  tournament_tree: { rounds?: { guild_a_id: number; guild_b_id: number; winner_guild_id: number | null; guild_a_name?: string; guild_b_name?: string; winner_guild_name?: string; status?: string }[] } | null;
  battle_bracket: BattleBracketView | null;
  last_match_summary: GuildBattleMatchSummary | null;
  my_platinum_incumbent_seat: number | null;
  can_claim_platinum_seats: number[];
  roster_ready: RosterReadySlot[];
  replay_acks_received: number;
  replay_acks_required: number;
  my_replay_ack_required: boolean;
  my_replay_ack_done: boolean;
  last_seat_results: { seat_index: number; result: string; winner_user_id?: number | null }[];
  in_battle_window?: boolean;
  battle_role?: string | null;
  can_enter_battle?: boolean;
  status_label?: string | null;
  next_battle_label?: string | null;
}

export interface GuildBattleInfoView {
  next_battle_at_utc: string;
  weekday_label: string;
  battle_hour_utc: number;
  battle_minute_utc: number;
  roster_size: number;
  can_manage_roster: boolean;
  battle_target_palace: string | null;
  roster: GuildRosterSlotView[];
  live: GuildBattleLiveView | null;
}

export interface PlatinumSeatView {
  seat_index: number;
  title: string;
  user_id: number | null;
  display_name: string | null;
  guild_name: string | null;
  is_npc: boolean;
  status_emoji: string;
}

export interface PalaceConquerorView {
  user_id: number;
  display_name: string;
  guild_name: string | null;
  role: string;
  week_key: string;
  created_at: string;
}

export interface PalaceDetailView {
  palace: string;
  name: string;
  icon: string;
  holder_guild_id: number | null;
  holder_guild_name: string | null;
  daily_gold: number;
  daily_contribution: number;
  daily_claim_available: boolean;
  daily_claimed_today: boolean;
  conquerors: PalaceConquerorView[];
  platinum_seats: PlatinumSeatView[];
}

export interface GuildOverview {
  unlocked: boolean;
  unlock_level: number;
  create_cost_gold: number;
  create_cost_diamonds: number;
  in_guild: boolean;
  my_guild: MyGuildView | null;
  pending_applications_count: number;
  palaces: GuildPalaceView[];
  battle: GuildBattleInfoView | null;
  platinum_seats: PlatinumSeatView[];
}

export interface GuildSearchResult {
  id: number;
  name: string;
  level: number;
  member_count: number;
  palace: string;
  has_pending_application: boolean;
}

export interface GuildMemberView {
  user_id: number;
  display_name: string;
  hero_class: string | null;
  role: string;
  contribution: number;
  joined_at: string;
}

export interface GuildApplicationView {
  id: number;
  user_id: number;
  display_name: string;
  hero_class: string | null;
  message: string | null;
  created_at: string;
}

export interface GuildShopItemView {
  item_id: string;
  name: string;
  description: string;
  contribution_cost: number;
  min_guild_level: number;
}

export interface GuildActionResult {
  ok: boolean;
  message: string;
  overview: GuildOverview;
}

export interface NpcFillerView {
  tier: number;
  gold_cost: number;
  stat_multiplier: number;
}

export interface GuildBattleLogEntry {
  id: number;
  week_key: string;
  palace: string;
  attacker_guild_name: string | null;
  defender_guild_name: string | null;
  winner_guild_name: string | null;
  skipped_reason: string | null;
  log: { round?: number; bracket?: string; team_a?: string[]; team_b?: string[]; winner?: string }[];
  created_at: string;
}

export interface PlatinumVoteView {
  id: number;
  title: string;
  description: string;
  status: string;
  yes_weight: number;
  no_weight: number;
  ends_at: string;
  created_at: string;
}

export interface GuildSkillTowerSkillView {
  code: string;
  display_name: string;
  current_level: number;
  max_level: number;
  next_cost_contribution: number | null;
}

export interface GuildSkillTowerResponse {
  enabled: boolean;
  contribution: number;
  skills: GuildSkillTowerSkillView[];
  active_while_in_guild: boolean;
}

export interface GuildTreeAttendeeView {
  user_id: number;
  display_name: string;
}

export interface GuildTreeStateResponse {
  enabled: boolean;
  active: boolean;
  can_start: boolean;
  starts_today: number;
  max_starts_per_day: number;
  ends_at: string | null;
  seconds_remaining: number;
  active_node: number | null;
  window_open_at: string | null;
  window_close_at: string | null;
  node_count: number;
  tap_window_sec: number;
  gather_display_sec: number;
  attendees: GuildTreeAttendeeView[];
}

export const guildApi = {
  overview: () => api.get<GuildOverview>("/guild/overview"),
  search: (q: string) => api.get<{ guilds: GuildSearchResult[] }>(`/guild/search?q=${encodeURIComponent(q)}`),
  create: (name: string) => api.post<GuildActionResult>("/guild/create", { name }),
  apply: (guild_id: number, message?: string) =>
    api.post<GuildActionResult>("/guild/apply", { guild_id, message: message ?? null }),
  applications: () => api.get<{ applications: GuildApplicationView[] }>("/guild/applications"),
  acceptApplication: (application_id: number) =>
    api.post<GuildActionResult>("/guild/applications/accept", { application_id }),
  rejectApplication: (application_id: number) =>
    api.post<GuildActionResult>("/guild/applications/reject", { application_id }),
  leave: () => api.post<GuildActionResult>("/guild/leave"),
  members: (guild_id: number) =>
    api.get<{ guild_id: number; guild_name: string; members: GuildMemberView[] }>(
      `/guild/members?guild_id=${guild_id}`,
    ),
  donateGold: (amount: number) => api.post<GuildActionResult>("/guild/donate-gold", { amount }),
  setBattleTarget: (target_palace: string) =>
    api.post<GuildActionResult>("/guild/battle/target", { target_palace }),
  setRoster: (slots: { slot: number; user_id?: number | null; npc_tier?: number | null }[]) =>
    api.post<GuildActionResult>("/guild/battle/roster", { slots }),
  shop: () =>
    api.get<{ items: GuildShopItemView[]; my_contribution: number; guild_level: number }>("/guild/shop"),
  buyShopItem: (item_id: string) => api.post<GuildActionResult>("/guild/shop/buy", { item_id }),
  npcFillers: () =>
    api.get<{
      fillers: NpcFillerView[];
      gold_treasury: number;
      can_purchase: boolean;
      battle_week_key: string | null;
    }>("/guild/npc-fillers"),
  buyNpc: (slot: number, tier: number) => api.post<GuildActionResult>("/guild/npc/buy", { slot, tier }),
  promoteMember: (target_user_id: number, role: string) =>
    api.post<GuildActionResult>("/guild/members/promote", { target_user_id, role }),
  presence: () => api.post<{ ok: boolean }>("/guild/presence"),
  battleLogs: (limit = 30) => api.get<{ battles: GuildBattleLogEntry[] }>(`/guild/battle/logs?limit=${limit}`),
  battleLive: () => api.get<{ live: GuildBattleLiveView }>("/guild/battle/live"),
  enterBattle: () => api.post<{ ok: boolean; message: string }>("/guild/battle/enter"),
  ackBattleReplay: (match_id: string) =>
    api.post<{ ok: boolean; message: string; complete?: boolean }>("/guild/battle/replay/ack", { match_id }),
  completePalaceWatch: () =>
    api.post<{ ok: boolean; message: string }>("/guild/battle/watch/complete"),
  claimPlatinumSeat: (seat_index: number) =>
    api.post<{ ok: boolean; message: string }>("/guild/platinum/claim-seat", { seat_index }),
  palaceDetail: (palace: string) => api.get<PalaceDetailView>(`/guild/palaces/${palace}`),
  claimPalaceDaily: (palace: string) => api.post<GuildActionResult>(`/guild/palaces/${palace}/claim-daily`),
  platinumVotes: () => api.get<{ votes: PlatinumVoteView[] }>("/guild/platinum/votes"),
  createPlatinumVote: (title: string, description: string, duration_hours = 48) =>
    api.post<{ id: number }>("/guild/platinum/votes", { title, description, duration_hours }),
  castPlatinumVote: (vote_id: number, choice: "yes" | "no" | "abstain") =>
    api.post<GuildActionResult>("/guild/platinum/votes/cast", { vote_id, choice }),
  skillTower: () => api.get<GuildSkillTowerResponse>("/guild/skill-tower"),
  upgradeSkillTower: (skill_code: string) =>
    api.post<GuildSkillTowerResponse>("/guild/skill-tower/upgrade", { skill_code }),
  treeState: () => api.get<GuildTreeStateResponse>("/guild/tree/state"),
  treeStart: () => api.post<GuildTreeStateResponse>("/guild/tree/start"),
  treeJoin: () => api.post<GuildTreeStateResponse>("/guild/tree/join"),
  treeLeave: () => api.post<GuildActionResult>("/guild/tree/leave"),
  treeHeartbeat: () => api.post<GuildTreeStateResponse>("/guild/tree/heartbeat"),
  treeTap: (node_index: number) =>
    api.post<{ ok: boolean; rewards: Record<string, number>; gather_display_sec: number }>(
      "/guild/tree/tap",
      { node_index },
    ),

  altarStatus: () => api.get<GuildAltarStatus>("/guild/altar/status"),
  altarSpin: () => api.post<GuildAltarSpinResult>("/guild/altar/spin"),
};

export interface GuildAltarRewardPreview {
  type: string;
  amount?: number | null;
  item_id?: string | null;
  quantity?: number | null;
  mount_code?: string | null;
  label: string;
  icon_hint: string;
}

export interface GuildAltarSegment {
  slot: number;
  reward_preview: GuildAltarRewardPreview;
}

export interface GuildAltarStatus {
  active: boolean;
  in_guild: boolean;
  free_spin_available: boolean;
  segments: GuildAltarSegment[];
  next_reset_utc: string;
}

export interface GuildAltarSpinResult {
  winning_slot_index: number;
  reward: GuildAltarRewardPreview;
  message: string;
  free_spin_available: boolean;
}
